import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react'

/**
 * THE FORM-FACTOR AXIS, decided once and read everywhere.
 *
 * The product is ONE SPA (DR-0042). It does not ship a phone build beside the
 * desktop one; it resolves WHICH KIND OF SCREEN it stands on and lets the
 * shared primitives draw themselves for it. Two answers exist: `desktop`, the
 * three-region workspace every existing proof describes, and `handheld`, the
 * one-region shell a thumb works. A tablet is not a third answer — it keeps
 * the stacked desktop layout the `shell` rung already draws.
 *
 * WHAT `handheld` MEANS is two facts at once: the viewport is at or under the
 * ladder's `handheld` rung AND the primary pointer is coarse. Width alone is
 * not enough — a narrow desktop window has a mouse, hover and a keyboard, and
 * the drawer-and-sheet shell would cost it the columns for nothing. Pointer
 * alone is not enough either — a touch laptop has the room for three regions.
 *
 * ONE READER. Pages never ask the viewport, never call `matchMedia` and never
 * name the form factor; they read this provider through the shared primitives
 * that render them, so a registry page renders cards on a phone with zero page
 * edits. `web/tests/form-factor.test.ts` refuses a page that names it directly.
 *
 * DESKTOP BY DEFAULT, and by construction. Where `matchMedia` is absent — jsdom,
 * and therefore the whole existing suite — the answer is `desktop`, which is
 * what keeps every current test the proof that desktop did not move. A test
 * that wants the other answer says so through `override`, or stubs
 * `matchMedia` to answer the query below; nothing about the provider has to be
 * mocked.
 */
export type FormFactor = 'desktop' | 'handheld'

/**
 * The `handheld` rung of the breakpoint ladder, repeated by hand. The ladder
 * lives in `web/tests/breakpoints.ts`, which product source cannot import, and
 * a stylesheet cannot read a custom property in a media query, so
 * `handheldShell.css` states the same width a third time. The rule test beside
 * the ladder fails the moment any of the three disagree.
 *
 * `30rem` rather than a device name: every phone held upright is under it and
 * no tablet is, at every reading scale, because the rung is in the reader's
 * own `rem`.
 */
export const HANDHELD_RUNG = '30rem'

/**
 * The one media query the axis is decided by. Exported so a test can stub
 * `matchMedia` against the exact string rather than a guess at it, and so the
 * stylesheet's copy can be held to it.
 */
export const HANDHELD_MEDIA_QUERY = `(max-width: ${HANDHELD_RUNG}) and (pointer: coarse)`

const FormFactorContext = createContext<FormFactor>('desktop')

/**
 * An override travels DOWN through nested providers: the test harness pins the
 * axis above the router, the shell mounts its own provider below it, and the
 * inner one must answer what the outer one was told rather than what the
 * missing `matchMedia` would say.
 */
const FormFactorOverrideContext = createContext<FormFactor | null>(null)

function mediaQueryList(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null
  }
  return window.matchMedia(HANDHELD_MEDIA_QUERY)
}

/**
 * The browser's answer, kept live: a phone turned sideways past the rung, or a
 * desktop window narrowed onto a touch screen, changes the answer without a
 * reload — `useSyncExternalStore` is the React-blessed way to read a value the
 * browser owns and can change under the tree.
 */
function useMediaFormFactor(): FormFactor {
  const list = useMemo(mediaQueryList, [])
  return useSyncExternalStore(
    (onChange) => {
      if (!list) {
        return () => {}
      }
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    },
    () => (list?.matches ? 'handheld' : 'desktop'),
    () => 'desktop',
  )
}

export function FormFactorProvider({
  override,
  children,
}: {
  /** Pins the axis regardless of the viewport. For tests, and only for tests. */
  override?: FormFactor
  children: ReactNode
}) {
  const outer = useContext(FormFactorOverrideContext)
  const media = useMediaFormFactor()
  const pinned = override ?? outer
  return (
    <FormFactorOverrideContext.Provider value={pinned}>
      <FormFactorContext.Provider value={pinned ?? media}>{children}</FormFactorContext.Provider>
    </FormFactorOverrideContext.Provider>
  )
}

/**
 * Which kind of screen the tree stands on. Outside a provider the answer is
 * `desktop`, so a shared primitive rendered in isolation draws what it always
 * drew.
 */
export function useFormFactor(): FormFactor {
  return useContext(FormFactorContext)
}
